import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ContactCardData, PhoneNumber, Email } from '../plugins/with-contact-cards';
import { PlusCircle, X, Camera } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  compressImage,
  validateMMSFile,
  validateEmail,
  validatePhoneNumber
} from '../utils/mms-utils';
import { useToast } from '@/hooks/use-toast';
import InputMask from 'react-input-mask';

interface ContactCardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: Omit<ContactCardData, 'id' | 'vcfString'>) => void;
  initialData?: ContactCardData;
}

interface FieldError {
  phoneNumbers: Record<number, string | null>;
  emails: Record<number, string | null>;
  image: string | null;
}

/**
 * Gets the initials from a name, taking up to two parts of the name.
 * For example:
 * - "John Doe" -> "JD"
 * - "John" -> "J"
 * - "John Middle Doe" -> "JD"
 */
function getInitials(name: string): string {
  if (!name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/**
 * Dialog for creating or editing a contact card.
 * Handles image upload, name, phone numbers, and email addresses.
 * Includes real-time validation and error feedback.
 *
 * Features:
 * - Proper state initialization from initialData
 * - Clean state reset on dialog close
 * - Real-time field validation with masked inputs
 * - Interactive avatar upload with preview
 * - Required field validation
 * - Responsive form layout
 * - Accessible form controls
 */
export function ContactCardDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData
}: ContactCardDialogProps) {
  const { toast } = useToast();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [name, setName] = React.useState('');
  const [image, setImage] = React.useState('');
  const [phoneNumbers, setPhoneNumbers] = React.useState<PhoneNumber[]>([
    { type: 'CELL', number: '' }
  ]);
  const [emails, setEmails] = React.useState<Email[]>([]);
  const [errors, setErrors] = React.useState<FieldError>({
    phoneNumbers: {},
    emails: {},
    image: null
  });

  // Initialize form state from initialData when dialog opens
  React.useEffect(() => {
    if (open && initialData) {
      setName(initialData.name);
      setImage(initialData.image ?? '');
      setPhoneNumbers(
        initialData.phoneNumbers.length > 0
          ? initialData.phoneNumbers
          : [{ type: 'CELL', number: '' }]
      );
      setEmails(initialData.emails);
      setErrors({
        phoneNumbers: {},
        emails: {},
        image: null
      });
    }
  }, [open, initialData]);

  // Reset form state when dialog closes
  React.useEffect(() => {
    if (!open) {
      setName('');
      setImage('');
      setPhoneNumbers([{ type: 'CELL', number: '' }]);
      setEmails([]);
      setErrors({
        phoneNumbers: {},
        emails: {},
        image: null
      });
    }
  }, [open]);

  const validateFields = (): boolean => {
    const newErrors: FieldError = {
      phoneNumbers: {},
      emails: {},
      image: null
    };

    let hasErrors = false;

    // Validate name
    if (!name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Name is required',
        variant: 'destructive'
      });
      return false;
    }

    // Validate phone numbers
    phoneNumbers.forEach((phone, index) => {
      if (phone.number.trim()) {
        const validation = validatePhoneNumber(phone.number);
        if (!validation.isValid) {
          newErrors.phoneNumbers[index] = validation.errorMessage;
          hasErrors = true;
        }
      }
    });

    // Require at least one phone number
    if (!phoneNumbers.some((p) => p.number.trim())) {
      toast({
        title: 'Validation Error',
        description: 'At least one phone number is required',
        variant: 'destructive'
      });
      return false;
    }

    // Validate emails
    emails.forEach((email, index) => {
      if (email.address.trim()) {
        const validation = validateEmail(email.address);
        if (!validation.isValid) {
          newErrors.emails[index] = validation.errorMessage;
          hasErrors = true;
        }
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateFields()) {
      toast({
        title: 'Validation Error',
        description: 'Please correct the errors in the form before submitting.',
        variant: 'destructive'
      });
      return;
    }

    onSubmit({
      name,
      image,
      phoneNumbers: phoneNumbers.filter((p) => p.number.trim()),
      emails: emails.filter((e) => e.address.trim())
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Clear previous errors
    setErrors((prev) => ({ ...prev, image: null }));

    const validation = validateMMSFile(file);
    if (!validation.isValid) {
      setErrors((prev) => ({ ...prev, image: validation.errorMessage }));
      return;
    }

    try {
      const compressed = await compressImage(file);
      setImage(compressed);
    } catch (_error) {
      setErrors((prev) => ({
        ...prev,
        image: 'Failed to process the image. Please try a different image.'
      }));
    }
  };

  const handlePhoneChange = (index: number, value: string) => {
    const newPhones = [...phoneNumbers];
    newPhones[index] = { ...newPhones[index], number: value };
    setPhoneNumbers(newPhones);

    // Real-time validation
    if (value.trim()) {
      const validation = validatePhoneNumber(value);
      setErrors((prev) => ({
        ...prev,
        phoneNumbers: {
          ...prev.phoneNumbers,
          [index]: validation.errorMessage
        }
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        phoneNumbers: {
          ...prev.phoneNumbers,
          [index]: null
        }
      }));
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = { ...newEmails[index], address: value };
    setEmails(newEmails);

    // Real-time validation
    if (value.trim()) {
      const validation = validateEmail(value);
      setErrors((prev) => ({
        ...prev,
        emails: {
          ...prev.emails,
          [index]: validation.errorMessage
        }
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        emails: {
          ...prev.emails,
          [index]: null
        }
      }));
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{initialData ? 'Edit Contact' : 'Create Contact'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Avatar Upload with Interactive Hover and Error State */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative group">
              {/* Avatar Container */}
              <div
                className={cn(
                  'relative h-24 w-24 rounded-full group',
                  'group-hover:ring-2 group-hover:ring-primary/50 group-hover:cursor-pointer',
                  errors.image && 'ring-2 ring-destructive'
                )}
                onClick={handleAvatarClick}
              >
                {/* Base Layer: Always visible fallback */}
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-secondary text-secondary-foreground text-xl font-medium">
                  {getInitials(name)}
                </div>

                {/* Middle Layer: Image when available */}
                {image && (
                  <img
                    src={image}
                    alt={name}
                    className="absolute inset-0 h-full w-full rounded-full object-cover"
                    onError={() => {
                      setImage(''); // Clear image state to show fallback
                      setErrors((prev) => ({
                        ...prev,
                        image: 'Failed to load the image. Please try again.'
                      }));
                    }}
                  />
                )}

                {/* Delete Button: Only when image exists */}
                {image && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setImage('');
                      setErrors((prev) => ({ ...prev, image: null }));
                      // Reset file input value so we can reselect the same file
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className={cn(
                      'absolute -top-1 -right-1 z-30 p-1.5 rounded-full',
                      'bg-background border border-border',
                      'hover:bg-destructive hover:border-destructive hover:text-white',
                      'transition-all duration-200',
                      'shadow-sm'
                    )}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}

                {/* Top Layer: Camera overlay when no image */}
                {!image && (
                  <div
                    className={cn(
                      'absolute inset-0 z-20 flex items-center justify-center rounded-full',
                      'bg-black/0 group-hover:bg-black/40',
                      'transition-all duration-200'
                    )}
                  >
                    <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-all duration-200" />
                  </div>
                )}
              </div>

              {/* Hidden file input */}
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => {
                  void handleImageUpload(e);
                }}
                className="hidden"
                id="avatar-upload"
              />
            </div>

            {/* Image Error Message */}
            {errors.image && (
              <p className="text-sm font-medium text-destructive-foreground text-center">
                {errors.image}
              </p>
            )}
          </div>

          {/* Name Input with Enhanced Validation */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="Contact name"
              required
              className={cn(
                'transition-all duration-200',
                'hover:border-input/80',
                'focus:ring-1 focus:ring-primary/30',
                !name.trim() && 'border-destructive focus:ring-destructive/30'
              )}
            />
          </div>

          {/* Phone Numbers with Masking */}
          <div className="space-y-2">
            <Label>Phone Numbers</Label>
            {phoneNumbers.map((phone, index) => (
              <div key={index} className="space-y-1">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <InputMask
                      mask="(999) 999-9999"
                      value={phone.number}
                      onChange={(e) => {
                        handlePhoneChange(index, e.target.value);
                      }}
                      placeholder="(555) 555-5555"
                    >
                      {(inputProps: any) => (
                        <Input
                          {...inputProps}
                          className={cn(
                            'transition-all duration-200',
                            'hover:border-input/80',
                            'focus:ring-1 focus:ring-primary/30',
                            errors.phoneNumbers[index] &&
                              'border-destructive focus:ring-destructive/30'
                          )}
                        />
                      )}
                    </InputMask>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setPhoneNumbers(phoneNumbers.filter((_, i) => i !== index));
                      setErrors((prev) => {
                        const { [index]: _, ...rest } = prev.phoneNumbers;
                        return { ...prev, phoneNumbers: rest };
                      });
                    }}
                    className={cn(
                      'h-10 w-10 transition-all duration-200',
                      'hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30'
                    )}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {errors.phoneNumbers[index] && (
                  <p className="text-xs text-destructive-foreground">
                    {errors.phoneNumbers[index]}
                  </p>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPhoneNumbers([...phoneNumbers, { type: 'CELL', number: '' }]);
              }}
              className={cn(
                'w-full transition-all duration-200',
                'hover:bg-primary/5 hover:border-primary/30',
                'active:scale-[0.98]'
              )}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Phone Number
            </Button>
          </div>

          {/* Emails with Enhanced Validation */}
          <div className="space-y-2">
            <Label>Email Addresses</Label>
            {emails.map((email, index) => (
              <div key={index} className="space-y-1">
                <div className="flex gap-2">
                  <Input
                    value={email.address}
                    onChange={(e) => {
                      handleEmailChange(index, e.target.value);
                    }}
                    placeholder="Email address"
                    type="email"
                    className={cn(
                      'flex-1 transition-all duration-200',
                      'hover:border-input/80',
                      'focus:ring-1 focus:ring-primary/30',
                      errors.emails[index] && 'border-destructive focus:ring-destructive/30'
                    )}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      setEmails(emails.filter((_, i) => i !== index));
                      setErrors((prev) => {
                        const { [index]: _, ...rest } = prev.emails;
                        return { ...prev, emails: rest };
                      });
                    }}
                    className={cn(
                      'h-10 w-10 transition-all duration-200',
                      'hover:bg-destructive/10 hover:text-destructive hover:border-destructive/30'
                    )}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {errors.emails[index] && (
                  <p className="text-xs text-destructive-foreground">{errors.emails[index]}</p>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEmails([...emails, { type: 'PERSONAL', address: '' }]);
              }}
              className={cn(
                'w-full transition-all duration-200',
                'hover:bg-primary/5 hover:border-primary/30',
                'active:scale-[0.98]'
              )}
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              Add Email Address
            </Button>
          </div>

          <Button
            type="submit"
            className={cn(
              'w-full transition-all duration-200',
              'hover:bg-primary/90',
              'active:scale-[0.98]'
            )}
          >
            {initialData ? 'Save Changes' : 'Create Contact'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
